import { newIdempotencyKey } from "@station/api-client";
import {
  DBS_ITEM_STATUS_LABELS,
  type DbsAgreement,
  type DbsItem,
  type DbsItemStatus,
  dbsItemStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DateInput,
  Dialog,
  EmptyState,
  FormField,
  Input,
  MoneyInput,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { HandCoins, Plus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { todayIstanbul } from "../../../features/accounting/shared.js";
import {
  CounterpartyCombobox,
  type CounterpartyOption,
} from "../../../features/counterparties/counterparty-combobox.js";
import {
  ReasonDialog,
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { useBankAccounts } from "../../../features/treasury/definitions.js";
import { TreasuryPage, useRefreshTreasury } from "../../../features/treasury/shared.js";
import { clampPercent, minorOf } from "../../../features/treasury/treasury-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";

export const Route = createFileRoute("/_app/finans/dbs")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: DbsPage,
});

function DbsPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [creatingAgreement, setCreatingAgreement] = useState(false);
  const [planning, setPlanning] = useState(false);
  return (
    <TreasuryPage
      title={t("menu.dbs")}
      description={t("treasury.dbs.description")}
      actions={
        permissions.has("treasury.dbs.manage") ? (
          <>
            <Button variant="outline" onClick={() => setCreatingAgreement(true)}>
              <Plus aria-hidden="true" />
              {t("treasury.dbs.createAgreement")}
            </Button>
            <Button onClick={() => setPlanning(true)}>
              <Plus aria-hidden="true" />
              {t("treasury.dbs.planItem")}
            </Button>
          </>
        ) : null
      }
    >
      {({ companyId }) => (
        <Dbs
          companyId={companyId}
          creatingAgreement={creatingAgreement}
          onCreatingAgreementChange={setCreatingAgreement}
          planning={planning}
          onPlanningChange={setPlanning}
        />
      )}
    </TreasuryPage>
  );
}

function Dbs({
  companyId,
  creatingAgreement,
  onCreatingAgreementChange,
  planning,
  onPlanningChange,
}: {
  companyId: string;
  creatingAgreement: boolean;
  onCreatingAgreementChange: (open: boolean) => void;
  planning: boolean;
  onPlanningChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const canManage = usePermissions().has("treasury.dbs.manage");
  const [editing, setEditing] = useState<DbsAgreement | null>(null);

  const agreements = useQuery({
    queryKey: queryKeys.treasury(companyId, "dbs-agreements"),
    queryFn: async () => (await api.GET("/dbs-agreements", { params: { query: { companyId } } })).data!.items,
  });

  return (
    <div className="grid gap-6">
      <section className="grid gap-3">
        <h2 className="text-base font-semibold">{t("treasury.dbs.agreementsTitle")}</h2>
        {agreements.error ? (
          <Alert tone="danger">{errorMessage(agreements.error)}</Alert>
        ) : agreements.isPending ? (
          <SkeletonRows rows={3} />
        ) : agreements.data.length === 0 ? (
          <EmptyState
            headingLevel={3}
            icon={<HandCoins />}
            title={t("treasury.dbs.emptyAgreementsTitle")}
            description={t("treasury.dbs.emptyAgreementsDescription")}
          />
        ) : (
          <ul className="grid gap-3 lg:grid-cols-2">
            {agreements.data.map((agreement) => (
              <li key={agreement.id}>
                <Card className="grid gap-3 p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <div className="grid gap-0.5">
                      <span className="flex flex-wrap items-center gap-2 font-medium">
                        {agreement.counterpartyName}
                        {agreement.isActive ? null : (
                          <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {agreement.bankName} · {agreement.bankAccountName}
                      </span>
                    </div>
                    {canManage ? (
                      <Button variant="ghost" size="sm" onClick={() => setEditing(agreement)}>
                        {t("common.edit")}
                      </Button>
                    ) : null}
                  </div>

                  <div className="grid gap-1">
                    {/* Limit doluluk çubuğu: görsel; kesin değerler altta ayrıca yazılır. */}
                    <div
                      className="h-2 overflow-hidden rounded-full bg-muted"
                      role="img"
                      aria-label={t("treasury.dbs.usageLabel", { percent: agreement.usagePercent })}
                    >
                      <div
                        className={
                          agreement.usagePercent >= 90 ? "h-full bg-destructive" : "h-full bg-primary"
                        }
                        style={{ width: `${clampPercent(agreement.usagePercent)}%` }}
                      />
                    </div>
                    <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                      <div className="flex items-baseline gap-2">
                        <dt className="text-muted-foreground">{t("treasury.dbs.limit")}</dt>
                        <dd className="font-medium">
                          <MoneyText value={agreement.limitAmount} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="text-muted-foreground">{t("treasury.dbs.used")}</dt>
                        <dd className="font-medium">
                          <MoneyText value={agreement.usedAmount} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="text-muted-foreground">{t("treasury.dbs.available")}</dt>
                        <dd className="font-medium">
                          <MoneyText value={agreement.availableAmount} />
                        </dd>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <dt className="text-muted-foreground">{t("treasury.dbs.usage")}</dt>
                        <dd className="font-medium">%{agreement.usagePercent}</dd>
                      </div>
                    </dl>
                  </div>
                  {agreement.note ? <p className="text-xs text-muted-foreground">{agreement.note}</p> : null}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <DbsItems companyId={companyId} />

      <AgreementDialog
        companyId={companyId}
        open={creatingAgreement || editing !== null}
        agreement={editing}
        onClose={() => {
          onCreatingAgreementChange(false);
          setEditing(null);
        }}
      />
      <PlanItemDialog
        companyId={companyId}
        open={planning}
        agreements={agreements.data ?? []}
        onClose={() => onPlanningChange(false)}
      />
    </div>
  );
}

/** DBS takvimi: vadesi yaklaşan kalemler; ödendi işaretleme ve iptal. */
function DbsItems({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const canManage = usePermissions().has("treasury.dbs.manage");
  const [status, setStatus] = useState<DbsItemStatus | "">("PLANNED");
  const [paying, setPaying] = useState<DbsItem | null>(null);
  const [cancelling, setCancelling] = useState<DbsItem | null>(null);
  const [paidOn, setPaidOn] = useState<string | null>(todayIstanbul());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const query = { companyId, limit: 25, ...(status ? { status } : {}) };
  const pages = useCursorPages(JSON.stringify(query));
  const items = useQuery({
    queryKey: queryKeys.treasury(companyId, "dbs-items", { ...query, cursor: pages.cursor ?? null }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/dbs-items", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
  });

  return (
    <section className="grid gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-base font-semibold">{t("treasury.dbs.calendarTitle")}</h2>
        <FormField label={t("common.status")} className="w-48">
          {(control) => (
            <Select
              {...control}
              value={status}
              onChange={(event) =>
                setStatus(event.target.value === "" ? "" : dbsItemStatusSchema.parse(event.target.value))
              }
            >
              <option value="">{t("common.all")}</option>
              {dbsItemStatusSchema.options.map((value) => (
                <option key={value} value={value}>
                  {DBS_ITEM_STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </div>

      {error ? <Alert tone="danger">{error}</Alert> : null}

      {items.error ? (
        <Alert tone="danger">{errorMessage(items.error)}</Alert>
      ) : items.isPending ? (
        <SkeletonRows rows={4} />
      ) : items.data.items.length === 0 ? (
        <EmptyState
          headingLevel={3}
          icon={<HandCoins />}
          title={t("treasury.dbs.emptyItemsTitle")}
          description={t("treasury.dbs.emptyItemsDescription")}
        />
      ) : (
        <SimpleTable
          caption={t("treasury.dbs.calendarTitle")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("treasury.dbs.dueDate")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.guarantees.beneficiary")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.fields.bank")}
              </th>
              <th scope="col" className={thClass}>
                {t("treasury.dbs.invoiceNo")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("treasury.fields.amount")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.status")}
              </th>
              <th scope="col" className={thClass}>
                {t("common.actions")}
              </th>
            </tr>
          }
        >
          {items.data.items.map((item) => (
            <tr key={item.id} className="border-t align-top">
              <td className={`${tdClass} tabular`}>{formatDate(item.dueDate)}</td>
              <td className={tdClass}>{item.counterpartyName}</td>
              <td className={tdClass}>{item.bankName}</td>
              <td className={tdClass}>{item.invoiceNo ?? "—"}</td>
              <td className={tdRightClass}>
                <MoneyText value={item.amount} className="font-medium" />
              </td>
              <td className={tdClass}>
                <StatusBadge
                  tone={
                    item.status === "PAID" ? "success" : item.status === "PLANNED" ? "warning" : "neutral"
                  }
                >
                  {DBS_ITEM_STATUS_LABELS[item.status]}
                </StatusBadge>
                {item.paidOn ? (
                  <>
                    <br />
                    <span className="tabular text-xs text-muted-foreground">{formatDate(item.paidOn)}</span>
                  </>
                ) : null}
              </td>
              <td className={tdClass}>
                {canManage && item.status === "PLANNED" ? (
                  <span className="flex flex-wrap gap-1">
                    <Button size="sm" onClick={() => setPaying(item)}>
                      {t("treasury.dbs.markPaid")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setCancelling(item)}>
                      {t("treasury.dbs.cancelItem")}
                    </Button>
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </SimpleTable>
      )}

      {pages.hasPrevious || items.data?.page.hasMore ? (
        <nav className="flex items-center justify-end gap-2" aria-label={t("treasury.dbs.pagination")}>
          <Button variant="outline" size="sm" onClick={pages.previous} disabled={!pages.hasPrevious}>
            {t("treasury.counts.previous")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => pages.next(items.data?.page.nextCursor)}
            disabled={!items.data?.page.hasMore}
          >
            {t("treasury.counts.next")}
          </Button>
        </nav>
      ) : null}

      <Dialog
        open={paying !== null}
        onOpenChange={(open) => !open && !busy && setPaying(null)}
        title={t("treasury.dbs.markPaidTitle")}
        description={t("treasury.dbs.markPaidDescription")}
        dismissible={!busy}
      >
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (!paying || !paidOn) return;
            setBusy(true);
            setError(null);
            void api
              .POST("/dbs-items/{id}/pay", {
                params: { path: { id: paying.id } },
                body: { paidOn, statementLineId: null, version: paying.version },
              })
              .then(async () => {
                await refresh(companyId);
                toast({ tone: "success", title: t("treasury.dbs.paid") });
                setPaying(null);
              })
              .catch((caught: unknown) => setError(errorMessage(caught)))
              .finally(() => setBusy(false));
          }}
        >
          <FormField label={t("treasury.dbs.paidOn")} required>
            {(control) => <DateInput {...control} value={paidOn} onValueChange={setPaidOn} />}
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPaying(null)} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy}>
              {t("treasury.dbs.markPaid")}
            </Button>
          </div>
        </form>
      </Dialog>

      <ReasonDialog
        open={cancelling !== null}
        onOpenChange={(open) => !open && setCancelling(null)}
        title={t("treasury.dbs.cancelItemTitle")}
        description={t("treasury.dbs.cancelItemDescription")}
        confirmLabel={t("treasury.dbs.cancelItem")}
        onConfirm={async (reason) => {
          await api.POST("/dbs-items/{id}/cancel", {
            params: { path: { id: cancelling!.id } },
            body: { reason, version: cancelling!.version },
          });
          await refresh(companyId);
          toast({ tone: "success", title: t("treasury.dbs.itemCancelled") });
          setCancelling(null);
        }}
      />
    </section>
  );
}

function AgreementDialog({
  companyId,
  open,
  agreement,
  onClose,
}: {
  companyId: string;
  open: boolean;
  agreement: DbsAgreement | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const accounts = useBankAccounts(companyId);
  const [bankAccountId, setBankAccountId] = useState("");
  const [counterparty, setCounterparty] = useState<CounterpartyOption | null>(null);
  const [limitAmount, setLimitAmount] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastId, setLastId] = useState<string | null>(null);
  if (open && (agreement?.id ?? null) !== lastId) {
    setLastId(agreement?.id ?? null);
    setError(null);
    setBankAccountId(agreement?.bankAccountId ?? "");
    setCounterparty(
      agreement ? { id: agreement.counterpartyId, code: "", name: agreement.counterpartyName } : null,
    );
    setLimitAmount(agreement?.limitAmount ?? null);
    setNote(agreement?.note ?? "");
    setIsActive(agreement?.isActive ?? true);
  }

  async function submit(): Promise<void> {
    if (!agreement && (!bankAccountId || !counterparty)) return setError(t("treasury.dbs.agreementMissing"));
    setBusy(true);
    setError(null);
    try {
      if (agreement)
        await api.PATCH("/dbs-agreements/{id}", {
          params: { path: { id: agreement.id } },
          body: {
            limitAmount: minorOf(limitAmount).toString(),
            note: note.trim() || null,
            isActive,
            version: agreement.version,
          },
        });
      else
        await api.POST("/dbs-agreements", {
          body: {
            companyId,
            bankAccountId,
            counterpartyId: counterparty!.id,
            limitAmount: minorOf(limitAmount).toString(),
            note: note.trim() || null,
          },
        });
      await refresh(companyId);
      toast({ tone: "success", title: t("treasury.dbs.agreementSaved") });
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && !busy && onClose()}
      title={agreement ? t("treasury.dbs.editAgreement") : t("treasury.dbs.createAgreement")}
      description={t("treasury.dbs.agreementDialogDescription")}
      className="sm:max-w-2xl"
      dismissible={!busy}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t("treasury.fields.bankAccount")} required>
            {(control) => (
              <Select
                {...control}
                value={bankAccountId}
                disabled={agreement !== null}
                onChange={(event) => setBankAccountId(event.target.value)}
              >
                <option value="">{t("common.select")}</option>
                {(accounts.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.bankName} · {account.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("treasury.dbs.distributor")} required>
            {(control) => (
              <CounterpartyCombobox
                {...control}
                companyId={companyId}
                value={counterparty}
                disabled={agreement !== null}
                onChange={setCounterparty}
              />
            )}
          </FormField>
          <FormField label={t("treasury.dbs.limit")} required>
            {(control) => <MoneyInput {...control} value={limitAmount} onValueChange={setLimitAmount} />}
          </FormField>
        </div>
        <FormField label={t("treasury.dbs.note")}>
          {(control) => (
            <Textarea
              {...control}
              rows={2}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </FormField>
        {agreement ? (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
            {t("common.active")}
          </label>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Kalem planlama: limit aşılıyorsa sunucu 409 `DBS_LIMIT_EXCEEDED` döner; kullanıcı "yine de planla" diyebilir. */
function PlanItemDialog({
  companyId,
  open,
  agreements,
  onClose,
}: {
  companyId: string;
  open: boolean;
  agreements: readonly DbsAgreement[];
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const [agreementId, setAgreementId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [dueDate, setDueDate] = useState<string | null>(todayIstanbul());
  const [amount, setAmount] = useState<string | null>(null);
  const [overLimit, setOverLimit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(allowOverLimit: boolean): Promise<void> {
    if (!agreementId) return setError(t("treasury.dbs.pickAgreement"));
    if (!dueDate) return setError(t("treasury.dbs.pickDueDate"));
    setBusy(true);
    setError(null);
    try {
      await api.POST("/dbs-items", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body: {
          agreementId,
          openItemId: null,
          invoiceNo: invoiceNo.trim() || null,
          dueDate,
          amount: minorOf(amount).toString(),
          allowOverLimit,
        },
      });
      await refresh(companyId);
      toast({ tone: "success", title: t("treasury.dbs.itemPlanned") });
      setInvoiceNo("");
      setAmount(null);
      setOverLimit(false);
      onClose();
    } catch (caught) {
      if (isApiError(caught, "DBS_LIMIT_EXCEEDED")) {
        setOverLimit(true);
        setError(t("treasury.dbs.limitExceeded"));
      } else setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) {
          setOverLimit(false);
          setError(null);
          onClose();
        }
      }}
      title={t("treasury.dbs.planItem")}
      description={t("treasury.dbs.planItemDescription")}
      className="sm:max-w-2xl"
      dismissible={!busy}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit(false);
        }}
      >
        {error ? <Alert tone={overLimit ? "warning" : "danger"}>{error}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t("treasury.dbs.agreement")} required>
            {(control) => (
              <Select
                {...control}
                value={agreementId}
                onChange={(event) => setAgreementId(event.target.value)}
              >
                <option value="">{t("common.select")}</option>
                {agreements
                  .filter((agreement) => agreement.isActive)
                  .map((agreement) => (
                    <option key={agreement.id} value={agreement.id}>
                      {agreement.counterpartyName} · {agreement.bankName}
                    </option>
                  ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("treasury.dbs.invoiceNo")}>
            {(control) => (
              <Input
                {...control}
                value={invoiceNo}
                maxLength={60}
                onChange={(event) => setInvoiceNo(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t("treasury.dbs.dueDate")} required>
            {(control) => <DateInput {...control} value={dueDate} onValueChange={setDueDate} />}
          </FormField>
          <FormField label={t("treasury.fields.amount")} required>
            {(control) => <MoneyInput {...control} value={amount} onValueChange={setAmount} />}
          </FormField>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          {overLimit ? (
            <Button variant="destructive" loading={busy} onClick={() => void submit(true)}>
              {t("treasury.dbs.planAnyway")}
            </Button>
          ) : null}
          <Button type="submit" loading={busy}>
            {t("treasury.dbs.planItem")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
